"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GalaAnalytics = void 0;
const class_transformer_1 = require("class-transformer");
const class_validator_1 = require("class-validator");
const dtos_1 = require("../../../gal-dtos/index");
const analytics_1 = require("../../../gal-dtos/types/analytics");
const logger_1 = require("../../../gal-lib-core/src/class/logger");
const config_1 = __importDefault(require("../config"));
const exposed_constants_1 = require("./exposed-constants");
const report_queue_1 = require("./report-queue");
const reporting_1 = require("./reporting");
const user_persisetence_1 = require("./user-persisetence");
class GalaAnalytics {
    constructor(args) {
        var _a, _b;
        this.args = args;
        this.validateArgs(args);
        this.reporting = this.createReporter(args);
        this.enabled = (_a = args.enabled) !== null && _a !== void 0 ? _a : true;
        if (args.user) {
            user_persisetence_1.UserPersistence.user = args.user;
        }
        if ((_b = args.disabledQueue) !== null && _b !== void 0 ? _b : true) {
            this.reportQueue = new report_queue_1.ReportQueue();
        }
    }
    get enabled() {
        return this.isEnabled;
    }
    set enabled(isEnabled) {
        if (!isEnabled && this.isEnabled) {
            logger_1.Logger.error('Cannot disable reporting once it has been enabled');
            return;
        }
        this.isEnabled = isEnabled;
        if (this.isEnabled) {
            logger_1.Logger.info('Enabled');
            this.processQueue();
        }
    }
    clearReportQueue() {
        logger_1.Logger.info('Report queue flushed');
        this.reportQueue.clear();
    }
    report(payload, asBeacon = false) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            GalaAnalytics.validateType(payload.type);
            payload = GalaAnalytics.payloadToInstance(payload.type, (0, class_transformer_1.instanceToPlain)(payload));
            yield GalaAnalytics.validateDTO(payload);
            const reportPayload = {
                details: payload,
                user: (_a = user_persisetence_1.UserPersistence.user) !== null && _a !== void 0 ? _a : '',
                timestamp: Date.now(),
            };
            if (this.isEnabled) {
                yield this.reporting.send(reportPayload, asBeacon);
            }
            else {
                if ((_b = this.args.disabledQueue) !== null && _b !== void 0 ? _b : true) {
                    logger_1.Logger.info(`Report type ${payload.type} queued`);
                }
                this.reportQueue.enqueue(reportPayload);
            }
            if (payload.type === dtos_1.GalEvents.LogoutDTO) {
                user_persisetence_1.UserPersistence.clearUser();
            }
        });
    }
    setUserId(userId) {
        user_persisetence_1.UserPersistence.user = userId;
        logger_1.Logger.info('User id set');
    }
    clearUserId() {
        user_persisetence_1.UserPersistence.user = null;
        logger_1.Logger.info('User id unset');
    }
    static allowedGroups(type) {
        var _a, _b;
        return (_b = (_a = dtos_1.dtos[type]) === null || _a === void 0 ? void 0 : _a.allowedGroups()) !== null && _b !== void 0 ? _b : [];
    }
    static payloadToInstance(type, payload) {
        const cls = dtos_1.dtos[type];
        const instance = (0, class_transformer_1.plainToInstance)(cls, Object.assign({ type }, payload));
        return instance;
    }
    static validateType(type) {
        if (!type)
            return;
        if (!Object.values(dtos_1.GalEvents).includes(type)) {
            throw new Error(`Could not submit report, invalid event type: ${type}`);
        }
    }
    static validateDTO(payload) {
        return __awaiter(this, void 0, void 0, function* () {
            let errors;
            try {
                errors = yield (0, class_validator_1.validate)(payload, {
                    skipMissingProperties: false,
                    skipUndefinedProperties: false,
                    skipNullProperties: false,
                    forbidUnknownValues: true,
                    enableDebugMessages: config_1.default.debug,
                    whitelist: true,
                    forbidNonWhitelisted: true,
                    validationError: {
                        target: true,
                        value: true,
                    },
                });
            }
            catch (err) {
                logger_1.Logger.handle(err);
                throw new Error('Could not submit report due to malformed payload');
            }
            if (errors.length) {
                const messages = [];
                errors.forEach((error) => {
                    var _a;
                    Object.entries((_a = error.constraints) !== null && _a !== void 0 ? _a : {}).forEach(([_, errorMsg]) => {
                        messages.push(`${errorMsg}`);
                    });
                });
                throw new Error(messages.join('; '));
            }
        });
    }
    processQueue() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            while ((_a = this.reportQueue) === null || _a === void 0 ? void 0 : _a.hasNext) {
                const next = this.reportQueue.next();
                if (next) {
                    next.user = (_b = user_persisetence_1.UserPersistence.user) !== null && _b !== void 0 ? _b : next === null || next === void 0 ? void 0 : next.user;
                    yield this.reporting.send(next);
                }
                yield new Promise((resolve) => setTimeout(resolve, config_1.default.eventQueueProcessBackoffMS));
            }
        });
    }
    validateArgs(args) {
        if (args.environment &&
            !Object.values(analytics_1.GalEnvironment).includes(args.environment)) {
            throw new Error(`Environment must be ${Object.values(analytics_1.GalEnvironment).join(',')}`);
        }
    }
    createReporter(args) {
        var _a;
        return new reporting_1.GalReport({
            reportingUri: args.reportingUri,
            publisherId: args.publisherId,
            reportingGroup: args.publisherGroup,
            obscure: args.useObscureEndpoint !== false,
            environment: (_a = args.environment) !== null && _a !== void 0 ? _a : undefined,
            fpEndpoint: args.fpEndpoint,
        });
    }
}
exports.GalaAnalytics = GalaAnalytics;
GalaAnalytics.constants = exposed_constants_1.ExposedConstants;
